// src/pages/CalendarsAdmin.js
import React, { useEffect, useMemo, useState } from 'react';
import styles from '../Styles/CalendarsAdmin.module.css';

import IconCalendar from '../Images/IconCalendar';
import IconScreen from '../Images/IconScreen';
import IconSauna from '../Images/IconSauna';
import IconWashing from '../Images/IconWashing';
import IconBed from '../Images/IconBed';
import IconJogging from '../Images/IconJogging';

import { useSelectedCompany } from '../Commoncontext/SelectedCompanyContext';
import { useVarauskalenterit } from '../Commoncontext/VarauskalenteritContext';
import { useToast } from '../Commoncontext/ToastContext';

const DAYS = ['Maanantai', 'Tiistai', 'Keskiviikko', 'Torstai', 'Perjantai', 'Lauantai', 'Sunnuntai'];

// YKSI LÄHDE tyypeille (id, label, icon)
const TYPES = [
  { id: 'sauna',         label: 'Sauna',         icon: <IconSauna /> },
  { id: 'etatyotila',    label: 'Etätyötila',    icon: <IconScreen /> },
  { id: 'pesutila',      label: 'Pesutila',      icon: <IconWashing /> },
  { id: 'kuivaushuone',  label: 'Kuivaushuone',  icon: <IconWashing /> },
  { id: 'harrastetila',  label: 'Harrastetila',  icon: <IconJogging /> },
  { id: 'vierashuone',   label: 'Vierashuone',   icon: <IconBed /> },
  { id: 'muu',           label: 'Muu',           icon: <IconCalendar /> },
];

function hourLabel(h) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(h)}:00 - ${pad(h + 1)}:00`;
}

// Mapataan backerin meta → typeId yksinkertaisesti label-matchilla
function typeIdFromMeta(meta) {
  const raw = String(meta?.tyyppi || meta?.nimi || '').toLowerCase();
  const found = TYPES.find(t => raw.includes(t.label.toLowerCase()));
  return found?.id || 'muu';
}
function iconForTypeId(id) {
  return TYPES.find(t => t.id === id)?.icon || <IconCalendar />;
}

// Kellonaikavalinnat 00–23
const TIME_OPTS = Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, '0')}:00`);

// ---- Apurit validointiin ----
function parseDate(value) {
  // value "YYYY-MM-DD" → Date (paikallinen keskiyö)
  if (!value) return null;
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d, 0, 0, 0, 0);
}
function parseTimeToHour(value) {
  // "HH:00" → 0..23
  if (!value) return null;
  const [hStr] = value.split(':');
  const h = Number(hStr);
  if (!Number.isFinite(h) || h < 0 || h > 23) return null;
  return h;
}
function compareYmd(a, b) {
  // vertailee vain päivää (00:00)
  if (!a || !b) return NaN;
  const ax = new Date(a.getFullYear(), a.getMonth(), a.getDate());
  const bx = new Date(b.getFullYear(), b.getMonth(), b.getDate());
  return ax.getTime() - bx.getTime();
}

export default function CalendarsAdmin() {
  const { selectedCompanyId } = useSelectedCompany();
  const {
    // listaus
    calendars,
    calendarsWithDrafts,
    getCalendarByIdCached,
    // päivitykset
    updateVarattavatajat,
    updateCalendarMeta,
    toArray,
    refreshCalendars,
    // luonnos-API
    addDraftCalendar,
    discardDraft,
    createCalendar,
  } = useVarauskalenterit();

  const { showToast } = useToast();

  // Lista valitulle yhtiölle
  const companyCalendars = useMemo(
    () => (calendarsWithDrafts && calendarsWithDrafts.length ? calendarsWithDrafts : calendars || []),
    [calendarsWithDrafts, calendars]
  );

  // Valittu kalenteri
  const [activeId, setActiveId] = useState(null);

  // Valitse automaattisesti ensimmäinen kun lista vaihtuu
  useEffect(() => {
    if (!companyCalendars?.length) {
      setActiveId(null);
      return;
    }
    if (!companyCalendars.some(c => c.id === activeId)) {
      setActiveId(companyCalendars[0].id);
    }
  }, [companyCalendars, activeId]);

  // Listasta aktiivisen pikaviite
  const activeCalListItem = useMemo(
    () => companyCalendars.find((c) => c.id === activeId) || (companyCalendars.length ? companyCalendars[0] : null),
    [activeId, companyCalendars]
  );

  // Meta-UI
  const [name, setName] = useState(activeCalListItem?.nimi || '');
  const [typeId, setTypeId] = useState(typeIdFromMeta(activeCalListItem || {}));
  const [maxPerWeek, setMaxPerWeek] = useState(7);
  const [maxPerUser, setMaxPerUser] = useState(3);
  const [cancelMaxHours, setCancelMaxHours] = useState(0);
  const [desc, setDesc] = useState('');

  // Tabit & 8h-ikkuna
  const [tab, setTab] = useState('open'); // 'open' | 'prices' | 'blocks'
  const [windowStart, setWindowStart] = useState(8);
  const windowHours = useMemo(
    () => Array.from({ length: 8 }, (_, i) => Math.max(0, Math.min(16, windowStart)) + i),
    [windowStart]
  );
  const shiftUp   = () => setWindowStart((prev) => (prev === 8 ? 0  : 8));
  const shiftDown = () => setWindowStart((prev) => (prev === 8 ? 16 : 16));

  // Avoinna/hinnat – UI-tila
  const [openMap, setOpenMap] = useState({});
  const [priceMap, setPriceMap] = useState({});

  // --- SULUT (UI) ---
  const [blocks, setBlocks] = useState([]); // {id?, title, startDate, startTime, endDate, endTime, repeat, untilDate, enabled}
  const [blockErrors, setBlockErrors] = useState({}); // index -> [virheet]

  const addBlock = () => {
    setBlocks(prev => [
      ...prev,
      {
        id: `tmp-${Date.now()}`,
        title: '',
        startDate: '',
        startTime: '18:00',
        endDate: '',
        endTime: '19:00',
        repeat: 'none',       // none | weekly | biweekly | monthly
        untilDate: '',
        enabled: true,
      }
    ]);
  };
  const updateBlock = (index, patch) => {
    setBlocks(prev => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      return next;
    });
    // kun käyttäjä muokkaa, puhdista mahdollinen virhe korostus kyseiseltä riviltä
    setBlockErrors(prev => {
      if (!prev[index]) return prev;
      const copy = { ...prev };
      delete copy[index];
      return copy;
    });
  };
  const removeBlock = (index) => {
    setBlocks(prev => prev.filter((_, i) => i !== index));
    setBlockErrors(prev => {
      if (!prev[index]) return prev;
      const copy = { ...prev };
      delete copy[index];
      return copy;
    });
  };

  // Kun aktiivinen kalenteri vaihtuu → nosta täysi data (cache)
  useEffect(() => {
    let ignore = false;
    (async () => {
      if (!activeId) return;
      try {
        const full = await getCalendarByIdCached(activeId);
        if (ignore || !full) return;

        // Meta → UI
        setName(full.nimi || '');
        setDesc(full.kuvaus || '');
        setTypeId(typeIdFromMeta(full));
        setMaxPerWeek(Number(full?.rajoitukset?.maxPerKayttajaPerVko || 7));
        setMaxPerUser(Number(full?.rajoitukset?.maxAvoimetPerKayttaja || 3));
        setCancelMaxHours(Number(full?.rajoitukset?.peruutusMaxTuntiAikaraja || 0));

        // Slotit → openMap, priceMap
        const nextOpen = {};
        const nextPrice = {};
        for (let h = 0; h < 24; h++) {
          const lbl = hourLabel(h);
          DAYS.forEach((d, di) => {
            const cell = full.varattavatajat?.[lbl]?.[d] || { open: false, price: 0 };
            nextOpen[`${di}-${h}`]  = !!cell.open;
            nextPrice[`${di}-${h}`] = Number(cell.price || 0);
          });
        }
        setOpenMap(nextOpen);
        setPriceMap(nextPrice);

        // Sulut – jos backend palauttaa 'blocks', mapataan tähän; muuten tyhjä
        setBlocks(Array.isArray(full.sulut) ? full.sulut.map(b => ({
          id: b.id || b._id || undefined,
          title: b.title || '',
          startDate: b.startDate || '',
          startTime: b.startTime || '18:00',
          endDate: b.endDate || '',
          endTime: b.endTime || '19:00',
          repeat: b.repeat || 'none',
          untilDate: b.untilDate || '',
          enabled: b.enabled !== false,
        })) : []);
        setBlockErrors({});
      } catch {
        // hiljaa
      }
    })();
    return () => { ignore = true; };
  }, [activeId, getCalendarByIdCached]);

  // Päivitä nimi/tyyppi kun listan aktiivinen itemi vaihtuu (UI)
  useEffect(() => {
    if (!activeCalListItem) return;
    setName(activeCalListItem.nimi || '');
    setTypeId(typeIdFromMeta(activeCalListItem));
  }, [activeCalListItem?.id]); // eslint-disable-line

  // Slotin togglaus / hinnan säätö
  const onToggleOpen = (di, h) => {
    const k = `${di}-${h}`;
    setOpenMap((prev) => ({ ...prev, [k]: !prev[k] }));
  };
  const incPrice = (di, h) => {
    const k = `${di}-${h}`;
    setPriceMap((prev) => ({ ...prev, [k]: (prev[k] || 0) + 1 }));
  };
  const decPrice = (di, h) => {
    const k = `${di}-${h}`;
    setPriceMap((prev) => ({ ...prev, [k]: Math.max(0, (prev[k] || 0) - 1) }));
  };

  // UUSI: lisää luonnos listaan ja aktivoi se
  const onNew = () => {
    if (!selectedCompanyId) return;
    const draft = addDraftCalendar(selectedCompanyId, {});
    setActiveId(draft.id);
    setName(draft.nimi || 'Uusi kalenteri');
    setTypeId('muu');
    setDesc('');
    setMaxPerUser(0);
    setMaxPerWeek(0);
    setCancelMaxHours(0);   

    // Kaikki kiinni/0€
    const o = {};
    const p = {};
    for (let h = 0; h < 24; h++) {
      for (let di = 0; di < 7; di++) {
        const k = `${di}-${h}`;
        o[k] = false;
        p[k] = 0;
      }
    }
    setOpenMap(o);
    setPriceMap(p);

    // Sulut tyhjänä luonnostilassa
    setBlocks([]);
    setBlockErrors({});
  };

  // Peruuta: jos luonnos → poista; jos olemassa oleva → lataa uudelleen viimeisin tallennettu state
  const onCancel = async () => {
    if (!activeId) return;
    if (activeId.startsWith('temp-')) {
      discardDraft(activeId);
      if (calendars?.length) {
        setActiveId(calendars[0].id);
      } else {
        setActiveId(null);
      }
      return;
    }
    try {
      const full = await getCalendarByIdCached(activeId);
      if (!full) return;
      setName(full.nimi || '');
      setDesc(full.kuvaus || '');
      setTypeId(typeIdFromMeta(full));
      setMaxPerWeek(Number(full?.rajoitukset?.maxPerKayttajaPerVko || 7));
      setMaxPerUser(Number(full?.rajoitukset?.maxAvoimetPerKayttaja || 3));
      setCancelMaxHours(Number(full?.rajoitukset?.peruutusMaxTuntiAikaraja || 0));

      const nextOpen = {};
      const nextPrice = {};
      for (let h = 0; h < 24; h++) {
        const lbl = hourLabel(h);
        DAYS.forEach((d, di) => {
          const cell = full.varattavatajat?.[lbl]?.[d] || { open: false, price: 0 };
          nextOpen[`${di}-${h}`]  = !!cell.open;
          nextPrice[`${di}-${h}`] = Number(cell.price || 0);
        });
      }
      setOpenMap(nextOpen);
      setPriceMap(nextPrice);

      setBlocks(Array.isArray(full.sulut) ? full.sulut : []);
      setBlockErrors({});
    } catch {
      // hiljaa
    }
  };

  // ---- VALIDOINNIT SULUILLE ----
  function validateBlocks(list) {
    const errors = {};
    list.forEach((b, idx) => {
      const rowErrs = [];

      const sd = parseDate(b.startDate);
      const ed = parseDate(b.endDate);
      const uh = parseDate(b.untilDate);
      const sh = parseTimeToHour(b.startTime);
      const eh = parseTimeToHour(b.endTime);

      // Pakolliset
      if (!b.title || !b.title.trim()) rowErrs.push('Selite puuttuu.');
      if (!sd) rowErrs.push('Alkupäivä puuttuu.');
      if (!ed) rowErrs.push('Loppupäivä puuttuu.');
      if (sh === null) rowErrs.push('Alkuaika puuttuu/virheellinen.');
      if (eh === null) rowErrs.push('Loppuaika puuttuu/virheellinen.');

      // Järjestys: pvm
      if (sd && ed && compareYmd(sd, ed) > 0) {
        rowErrs.push('Loppupäivä ei voi olla ennen alkupäivää.');
      }

      // Jos sama päivä → alkutunnin < lopputunnin
      if (sd && ed && compareYmd(sd, ed) === 0 && sh !== null && eh !== null) {
        if (sh >= eh) rowErrs.push('Samana päivänä loppuajan on oltava alkuaikaa myöhempi.');
      }

      // Toisto vaatii untilDate
      if (b.repeat && b.repeat !== 'none') {
        if (!uh) {
          rowErrs.push('Toistuva sulku vaatii päättyy viimeistään -päivämäärän.');
        } else if (sd && compareYmd(uh, sd) < 0) {
          rowErrs.push('Toistuvan sulun päättymispäivä ei voi olla ennen alkupäivää.');
        }
      }

      if (rowErrs.length) {
        errors[idx] = rowErrs;
      }
    });

    return {
      ok: Object.keys(errors).length === 0,
      errors,
    };
  }

  // Tallenna (meta + slots) – huomioi luonnos vs. olemassa oleva
  async function onSave() {
    if (!activeId) return;

    // 1) validoi sulut (estää tallennuksen, näyttää toastin ja korostaa rivit)
    const { ok, errors } = validateBlocks(blocks);
    if (!ok) {
      setBlockErrors(errors);
      // Näytä ensimmäinen virheilmoitus käyttäjälle
      const firstIdx = Number(Object.keys(errors)[0]);
      const firstMsg = errors[firstIdx][0] || 'Tarkista sulkujen tiedot.';
      showToast(firstMsg, 'error');
      return;
    }
    setBlockErrors({});

    try {
      const typeLabel = TYPES.find(t => t.id === typeId)?.label || 'Muu';

      // UI → varattavatajat (array Functionsille)
      const vmap = {};
      for (let h = 0; h < 24; h++) {
        const lbl = hourLabel(h);
        vmap[lbl] = {};
        DAYS.forEach((d, di) => {
          const k = `${di}-${h}`;
          vmap[lbl][d] = { open: !!openMap[k], price: Number(priceMap[k] || 0) };
        });
      }
      const arr = toArray(vmap);

      if (activeId.startsWith('temp-')) {
        // LUONNOS → LUO UUSI
        const payload = {
          nimi: name,
          tyyppi: typeLabel,
          kuvaus: desc,
          rajoitukset: {
            maxAvoimetPerKayttaja: Number(maxPerUser || 0),
            maxPerKayttajaPerVko: Number(maxPerWeek || 0),
            peruutusMaxTuntiAikaraja: Number(cancelMaxHours || 0),
          },
          varattavatajatArray: arr,
          talonyhtiöt: [selectedCompanyId],
          // HUOM: blocks-persistointi hoidetaan backendin valmistuessa; validointi tehty jo nyt
          sulut: blocks,
        };
        const newId = await createCalendar(payload);
        discardDraft(activeId);
        await refreshCalendars();
        setActiveId(newId);
        showToast('Kalenteri luotu.', 'success');
        return;
      }

      // OLEMASSA OLEVA → päivitys
      const patch = {
        nimi: name,
        tyyppi: typeLabel,
        kuvaus: desc,
        rajoitukset: {
          maxAvoimetPerKayttaja: Number(maxPerUser || 0),
          maxPerKayttajaPerVko: Number(maxPerWeek || 0),
          peruutusMaxTuntiAikaraja: Number(cancelMaxHours || 0),
        },
        // nyt myös SULUT talteen:
        sulut: blocks,
      };
      await updateCalendarMeta(activeId, patch);
      await updateVarattavatajat(activeId, arr);
      // blocks: kun backend endpoint valmis → kutsu tähän (ja päivitä konteksti)
      await refreshCalendars();
      showToast('Tallennus onnistui.', 'success');
    } catch (e) {
      showToast('Tallennus epäonnistui. Yritä uudelleen.', 'error');
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {/* Title */}
        <div className={styles.titleRow}>
          <div className={styles.titleLeft}>
            <IconCalendar />
            <h1>Varauskalenterien hallinta</h1>
          </div>
          <button type="button" className={styles.newBtn} onClick={onNew}>
            + Uusi
          </button>
        </div>

        {/* two columns */}
        <div className={styles.headerGrid}>
          {/* LEFT: list */}
          <div className={styles.calListCard}>
            <ul className={styles.calList}>
              {companyCalendars.map((c) => {
                const id = typeIdFromMeta(c);
                return (
                  <li
                    key={c.id}
                    className={`${styles.calRow} ${activeId === c.id ? styles.active : ''}`}
                    onClick={() => setActiveId(c.id)}
                    title={c.id.startsWith('temp-') ? 'Luonnos' : ''}
                  >
                    <span className={styles.iconCell}>{iconForTypeId(id)}</span>
                    <span className={styles.nameCell}>
                      {c.nimi}{c.id.startsWith('temp-') ? ' (Luonnos)' : ''}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* RIGHT: meta form */}
          <div className={styles.metaCard}>
            <div className={styles.metaRow}>
              <div className={styles.inputBlock}>
                <label>Nimi</label>
                <input
                  className={styles.input}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className={styles.inputBlock}>
                <label>Tyyppi</label>
                <select
                  className={styles.input}
                  value={typeId}
                  onChange={(e) => setTypeId(e.target.value)}
                >
                  {TYPES.map((t) => (
                    <option key={t.id} value={t.id}>{t.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className={styles.metaRow3}>
              <div className={styles.inputBlock}>
                <label>Max varauksia käyttäjä / vko</label>
                <input
                  className={styles.input}
                  type="number"
                  min={0}
                  value={maxPerWeek}
                  onChange={(e) => setMaxPerWeek(Number(e.target.value))}
                />
              </div>
              <div className={styles.inputBlock}>
                <label>Max avoimia varauksia / käyttäjä</label>
                <input
                  className={styles.input}
                  type="number"
                  min={0}
                  value={maxPerUser}
                  onChange={(e) => setMaxPerUser(Number(e.target.value))}
                />
              </div>
              <div className={styles.inputBlock}>
                <label>Peruutusraja (h)</label>
                <input
                  className={styles.input}
                  type="number"
                  min={0}
                  value={cancelMaxHours}
                  onChange={(e) => setCancelMaxHours(Number(e.target.value))}
                />
              </div>
            </div>

            <div className={styles.metaRow}>
              <div className={`${styles.inputBlock} ${styles.descBlock}`}>
                <label>Kuvaus</label>
                <textarea
                  className={`${styles.input} ${styles.textarea}`}
                  placeholder="Kuvaile lyhyesti esim. tilan käyttötarkoitus…"
                  value={desc}
                  onChange={(e) => setDesc(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* tabs */}
        <div className={styles.tabsRow}>
          <button
            type="button"
            className={`${styles.tab} ${tab === 'open' ? styles.tabActive : ''}`}
            onClick={() => setTab('open')}
          >
            Avoimet ajat
          </button>
          <button
            type="button"
            className={`${styles.tab} ${tab === 'prices' ? styles.tabActive : ''}`}
            onClick={() => setTab('prices')}
          >
            Hinnat
          </button>
          <button
            type="button"
            className={`${styles.tab} ${tab === 'blocks' ? styles.tabActive : ''}`}
            onClick={() => setTab('blocks')}
          >
            Sulut
          </button>

          {tab !== 'blocks' && (
            <div className={styles.windowControls}>
              <span className={styles.windowLabel}>
                Klo {String(windowStart).padStart(2, '0')}–{String(windowStart + 8).padStart(2, '0')}
              </span>
              <button type="button" className={styles.shiftBtn} onClick={shiftUp}   title="Siirry aiempaan">▲</button>
              <button type="button" className={styles.shiftBtn} onClick={shiftDown} title="Siirry myöhäisempään">▼</button>
            </div>
          )}
        </div>

        {/* grid content */}
        <div className={styles.gridCard}>
          {tab === 'open' && (
            <>
              <div className={styles.gridHeader}>
                {DAYS.map((d) => <div key={d} className={styles.gridHeaderCol}>{d}</div>)}
              </div>
              <div className={styles.gridBody}>
                {windowHours.map((h) => (
                  <div key={h} className={styles.gridRow}>
                    {DAYS.map((_, di) => {
                      const k = `${di}-${h}`;
                      const isOpen = !!openMap[k];
                      return (
                        <button
                          type="button"
                          key={k}
                          className={`${styles.slot} ${isOpen ? styles.slotOpen : styles.slotClosed}`}
                          onClick={() => onToggleOpen(di, h)}
                          title={hourLabel(h)}
                        >
                          {hourLabel(h)}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            </>
          )}

          {tab === 'prices' && (
            <>
              <div className={`${styles.gridHeader} ${styles.pricesHeader}`}>
                <div className={styles.timeHeaderCell}>Aika</div>
                {DAYS.map((d) => <div key={d} className={styles.gridHeaderCol}>{d}</div>)}
              </div>
              <div className={styles.gridBody}>
                {windowHours.map((h) => (
                  <div key={h} className={`${styles.gridRow} ${styles.priceRow}`}>
                    <div className={styles.timeLabelCell}>{hourLabel(h)}</div>
                    {DAYS.map((_, di) => {
                      const k = `${di}-${h}`;
                      const val = priceMap[k] ?? 0;
                      const isOpen = !!openMap[k]; // näytä vihreänä jos avoin Avoimet- välilehdellä
                      return (
                        <div key={k} className={`${styles.priceCell} ${isOpen ? styles.slotOpen : ''}`}>
                          <button
                            type="button"
                            className={styles.priceArrow}
                            onClick={() => incPrice(di, h)}
                            title="Korota hintaa"
                          >
                            ▲
                          </button>
                          <span className={styles.priceText}>{val} €</span>
                          <button
                            type="button"
                            className={styles.priceArrow}
                            onClick={() => decPrice(di, h)}
                            title="Laske hintaa"
                          >
                            ▼
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </>
          )}

          {tab === 'blocks' && (
            <div className={styles.blocksCard}>
              <div className={styles.blocksTopBar}>
                <button type="button" className={styles.addBlockBtn} onClick={addBlock}>
                  + Lisää sulku
                </button>
              </div>

              <div className={styles.blocksHeader}>
                <div>Selite</div>
                <div>Alkaa</div>
                <div></div>
                <div>Päättyy</div>
                <div></div>
                <div>Toistuu</div>
                <div>Päättyy viimeistään</div>
                <div></div>
              </div>

              <div className={styles.blocksList}>
                {blocks.map((b, i) => {
                  const hasErr = !!blockErrors[i];
                  return (
                    <div key={b.id ?? i} className={`${styles.blockRow} ${hasErr ? styles.blockRowError : ''}`}>
                      <input
                        className={`${styles.input} ${styles.blockInput}`}
                        placeholder="Esim. Lenkkisauna"
                        value={b.title || ''}
                        onChange={(e) => updateBlock(i, { title: e.target.value })}
                      />

                      <input
                        type="date"
                        className={`${styles.input} ${styles.blockInput}`}
                        value={b.startDate || ''}
                        onChange={(e) => updateBlock(i, { startDate: e.target.value })}
                      />

                      <select
                        className={`${styles.input} ${styles.blockInput}`}
                        value={b.startTime || '18:00'}
                        onChange={(e) => updateBlock(i, { startTime: e.target.value })}
                      >
                        {TIME_OPTS.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>

                      <input
                        type="date"
                        className={`${styles.input} ${styles.blockInput}`}
                        value={b.endDate || ''}
                        onChange={(e) => updateBlock(i, { endDate: e.target.value })}
                      />

                      <select
                        className={`${styles.input} ${styles.blockInput}`}
                        value={b.endTime || '19:00'}
                        onChange={(e) => updateBlock(i, { endTime: e.target.value })}
                      >
                        {TIME_OPTS.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>

                      <select
                        className={`${styles.input} ${styles.blockInput}`}
                        value={b.repeat || 'none'}
                        onChange={(e) => updateBlock(i, { repeat: e.target.value })}
                      >
                        <option value="none">Ei toistu</option>
                        <option value="weekly">Viikoittain</option>
                        <option value="biweekly">Joka toinen viikko</option>
                        <option value="monthly">Kuukausittain</option>
                      </select>

                      <input
                        type="date"
                        className={`${styles.input} ${styles.blockInput}`}
                        value={b.untilDate || ''}
                        onChange={(e) => updateBlock(i, { untilDate: e.target.value })}
                      />

                      <button
                        type="button"
                        className={styles.deleteBtn}
                        onClick={() => removeBlock(i)}
                        title="Poista sulku"
                      >
                        Poista
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Rivi-virheiden tekstinäyttö (valinnainen, mutta hyödyllinen debugiin) */}
              {Object.keys(blockErrors).length > 0 && (
                <div style={{ marginTop: 8 }}>
                  {Object.entries(blockErrors).map(([i, msgs]) => (
                    <div key={i} style={{ color: '#ffb3b3', fontSize: 12, marginTop: 2 }}>
                      Rivi {Number(i) + 1}: {(msgs || []).join(' ')}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className={styles.actionsRow}>
          <button className={styles.newBtn} type="button" onClick={onSave}>Tallenna</button>
          <button className={styles.cancelBtn} type="button" onClick={onCancel}>Peruuta</button>
        </div>
      </div>
    </div>
  );
}
